<template>
  <div class="container">
    <BasicTitle :title="pageTitle" />
    <BasicSubtitle subtitle="Los campos marcados con (*) son obligatorios." />
    <div class="container-form">
      <BasicLabel label="Número de cuenta *" />
      <BorderInput
        v-model="form.accountNumber"
        label="Número de cuenta *"
        maxlength="40"
      />
      <FormError :show="Boolean(errors.accountNumber)" :message="errors.accountNumber" />

      <BasicLabel label="Banco *" />
      <BorderSelect
        v-model="form.bankId"
        label="Banco *"
        :options="bankOptions"
        placeholder="Seleccione"
      />
      <FormError :show="Boolean(errors.bankId)" :message="errors.bankId" />

      <BasicLabel label="Moneda *" />
      <BorderSelect
        v-model="form.currencyId"
        label="Moneda *"
        :options="currencyOptions"
        placeholder="Seleccione"
      />
      <FormError :show="Boolean(errors.currencyId)" :message="errors.currencyId" />

      <BasicLabel label="Saldo inicial *" />
      <BorderInput
        v-model="form.initialBalance"
        label="Saldo inicial *"
        type="number"
        step="0.01"
        min="0"
      />
      <FormError :show="Boolean(errors.initialBalance)" :message="errors.initialBalance" />

      <BasicLabel label="Documento del titular *" />
      <BorderInput v-model="form.document" label="Documento del titular *" maxlength="25" />
      <FormError :show="Boolean(errors.document)" :message="errors.document" />

      <BasicLabel label="Fecha inicial del saldo *" />
      <BorderInput
        v-model="form.startDate"
        label="Fecha inicial del saldo *"
        type="date"
        :max="maxDate"
        @change="validateDate"
      />
      <FormError :show="Boolean(errors.startDate)" :message="errors.startDate" />
    </div>
    <PrimaryButton :label="primaryButtonLabel" :click="onSave" />
  </div>
</template>

<script>
import { computed, reactive, ref, onMounted, watch } from "vue";
import { useRouter } from "vue-router";
import store from "../../store";
import BasicTitle from "../../widgets/title/BasicTitle";
import BasicSubtitle from "../../widgets/subtitle/BasicSubtitle";
import BasicLabel from "../../widgets/label/BasicLabel";
import BorderInput from "../../widgets/input/BorderInput";
import BorderSelect from "../../widgets/select/BorderSelect";
import FormError from "../../widgets/error/FormError";
import PrimaryButton from "../../widgets/button/PrimaryButton";
import { openMode, actions } from "../../constants";
import { toOptionList } from "../../utils/options";

const createEmptyErrors = () => ({
  accountNumber: "",
  bankId: "",
  currencyId: "",
  initialBalance: "",
  document: "",
  startDate: "",
});

export default {
  components: {
    BasicTitle,
    BasicSubtitle,
    BasicLabel,
    BorderInput,
    BorderSelect,
    FormError,
    PrimaryButton,
  },
  setup() {
    const router = useRouter();

    const selectedBuilding = computed(() => store.state.general.selectedBuilding);
    const buildingId = computed(() => {
      const value = selectedBuilding.value;
      if (!value) return null;
      return typeof value === "object" ? value.id : value;
    });

    const isEditMode = computed(() => store.state.openMode === openMode.MODIFY);
    const pageTitle = computed(() => (isEditMode.value ? "Editar cuenta bancaria" : "Nueva cuenta bancaria"));
    const primaryButtonLabel = computed(() => (isEditMode.value ? "Actualizar" : "Guardar"));

    const form = reactive({
      id: null,
      accountNumber: "",
      bankId: "",
      currencyId: "",
      initialBalance: "",
      startDate: "",
      document: "",
    });

    const errors = reactive(createEmptyErrors());
    const maxDate = computed(() => new Date().toISOString().slice(0, 10));

    const bankOptions = computed(() =>
      toOptionList(store.state.general.bank, {
        valueKey: "id",
        labelFn: (item) => item.name,
      })
    );

    const currencyOptions = computed(() =>
      toOptionList(store.state.general.currencies, {
        labelFn: (item) => [item.code, item.name].filter(Boolean).join(" - "),
      })
    );

    const selectedBankAccountId = computed(
      () => store.state.bankAccount.selectedBankAccounts?.[0]?.id || store.state.bankAccount.current?.id || null
    );

    const clearErrors = () => {
      Object.keys(errors).forEach((key) => {
        errors[key] = "";
      });
    };

    const resetForm = () => {
      form.id = null;
      form.accountNumber = "";
      form.bankId = "";
      form.currencyId = "";
      form.initialBalance = "";
      form.startDate = "";
      form.document = "";
      clearErrors();
    };

    const populateForm = (bankAccount) => {
      if (!bankAccount) return;
      form.id = bankAccount.id ?? null;
      form.accountNumber = bankAccount.accountNumber || "";
      form.bankId = bankAccount.bankId ? String(bankAccount.bankId) : "";
      form.currencyId = bankAccount.currencyId ? String(bankAccount.currencyId) : "";
      form.initialBalance = bankAccount.initialBalance !== undefined ? String(bankAccount.initialBalance) : "";
      form.startDate = bankAccount.startDate || "";
      form.document = bankAccount.document || "";
      clearErrors();
    };

    const ensureGeneralData = async () => {
      if (!store.state.general.bank?.length || !store.state.general.currencies?.length) {
        await store.dispatch("GENERAL_DATA");
      }
    };

    const loadClosures = async (id) => {
      await store.dispatch(actions.commonExpenseActions.findAllClosure, { buildingId: id });
    };

    const loadBankAccount = async () => {
      if (!isEditMode.value || !selectedBankAccountId.value) {
        resetForm();
        return;
      }
      store.commit("setLoading", true);
      const response = await store.dispatch(actions.bankaccountActions.findOne, {
        id: selectedBankAccountId.value,
        buildingId: buildingId.value,
      });
      store.commit("setLoading", false);
      if (response?.ok) {
        populateForm(response.data);
      } else {
        router.push("/contabilidad/cuenta-bancaria/listado");
        store.commit("setToast", {
          type: "error",
          message: response?.message || "No fue posible cargar la cuenta seleccionada.",
        });
      }
    };

    const isDateAllowed = (dateStr) => {
      if (!dateStr) return false;
      const selected = new Date(dateStr);
      if (Number.isNaN(selected.getTime())) return false;
      const today = new Date(maxDate.value);
      if (selected > today) return false;
      const closures = store.state.commonExpense?.closures || [];
      const month = selected.getUTCMonth() + 1;
      const year = selected.getUTCFullYear();
      const blocked = closures.some(
        (closure) =>
          Number(closure.buildingId) === Number(buildingId.value) &&
          Number(closure.month) === month &&
          Number(closure.year) === year &&
          closure.status === "closed"
      );
      return !blocked;
    };

    const validate = () => {
      clearErrors();
      if (!form.accountNumber) {
        errors.accountNumber = "Ingrese el nómero de cuenta";
      }
      if (!form.bankId) {
        errors.bankId = "Seleccione un banco";
      }
      if (!form.currencyId) {
        errors.currencyId = "Seleccione una moneda";
      }
      const amountValue = Number(form.initialBalance);
      if (!Number.isFinite(amountValue) || amountValue < 0) {
        errors.initialBalance = "Ingrese un monto mayor o igual a cero";
      }
      if (!form.document) {
        errors.document = "Ingrese el documento";
      }
      if (!form.startDate) {
        errors.startDate = "Seleccione la fecha inicial";
      } else if (!isDateAllowed(form.startDate)) {
        errors.startDate = "La fecha no es válida para el edificio";
      }
      return !Object.values(errors).some(Boolean);
    };

    const validateDate = () => {
      if (!form.startDate) {
        errors.startDate = "Seleccione la fecha inicial";
      } else if (!isDateAllowed(form.startDate)) {
        errors.startDate = "La fecha no es válida para el edificio";
      } else {
        errors.startDate = "";
      }
    };

    const buildPayload = () => {
      const amountValue = Number(form.initialBalance);
      return {
        buildingId: buildingId.value,
        accountNumber: form.accountNumber.trim(),
        bankId: Number(form.bankId),
        currencyId: Number(form.currencyId),
        initialBalance: Number.isFinite(amountValue) ? amountValue : 0,
        startDate: form.startDate,
        document: form.document.trim(),
      };
    };

    const onSave = async () => {
      if (!validate()) return;
      if (!buildingId.value) {
        store.commit("setToast", {
          type: "error",
          message: "Seleccione un edificio para continuar.",
        });
        return;
      }

      store.commit("setLoading", true);
      try {
        const payload = buildPayload();
        let response;
        if (isEditMode.value && form.id) {
          response = await store.dispatch(actions.bankaccountActions.update, {
            id: form.id,
            ...payload,
          });
        } else {
          response = await store.dispatch(actions.bankaccountActions.create, payload);
        }
        if (response?.ok) {
          store.commit("setToast", {
            type: "success",
            message: isEditMode.value
              ? "Cuenta bancaria actualizada correctamente."
              : "Cuenta bancaria creada correctamente.",
          });
          store.commit("setSelectedBankAccounts", []);
          router.push("/contabilidad/cuenta-bancaria/listado");
        } else {
          store.commit("setToast", {
            type: "error",
            message: response?.message || "No fue posible guardar la cuenta bancaria.",
          });
        }
      } catch (error) {
        console.error(error);
        store.commit("setToast", {
          type: "error",
          message: "Ocurrió un error al guardar la cuenta bancaria.",
        });
      } finally {
        store.commit("setLoading", false);
      }
    };

    onMounted(async () => {
      store.commit("setLoading", true);
      try {
        await ensureGeneralData();
        if (buildingId.value) {
          await loadClosures(buildingId.value);
        }
        if (isEditMode.value) {
          await loadBankAccount();
        } else {
          resetForm();
        }
      } finally {
        store.commit("setLoading", false);
      }
    });

    watch(buildingId, async (value, oldValue) => {
      if (value && value !== oldValue) {
        store.commit("setLoading", true);
        try {
          await loadClosures(value);
          if (!isEditMode.value) {
            resetForm();
          }
        } finally {
          store.commit("setLoading", false);
        }
      }
    });

    watch(
      () => form.accountNumber,
      () => {
        if (form.accountNumber) errors.accountNumber = "";
      }
    );

    watch(
      () => form.bankId,
      () => {
        if (form.bankId) errors.bankId = "";
      }
    );

    watch(
      () => form.currencyId,
      () => {
        if (form.currencyId) errors.currencyId = "";
      }
    );

    watch(
      () => form.initialBalance,
      () => {
        const amountValue = Number(form.initialBalance);
        if (Number.isFinite(amountValue) && amountValue >= 0) {
          errors.initialBalance = "";
        }
      }
    );

    watch(
      () => form.document,
      () => {
        if (form.document) errors.document = "";
      }
    );

    watch(
      () => form.startDate,
      () => {
        if (form.startDate) validateDate();
      }
    );

    return {
      store,
      form,
      errors,
      pageTitle,
      primaryButtonLabel,
      bankOptions,
      currencyOptions,
      maxDate,
      validateDate,
      onSave,
    };
  },
};
</script>

<style scoped>
.container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.container-form {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 2rem 0;
  width: 100%;
  max-width: 600px;
}

.container-form > * {
  width: 100%;
  margin-bottom: 1.5rem;
}

.container-form > *:last-child {
  margin-bottom: 0;
}
</style>
